import { cn } from "@/lib/utils";
import { ChevronDown, ChevronRight } from "lucide-react";
import { MouseEvent, ReactNode } from "react";

export interface StatChipProps {
  /** Caption, e.g. "Price". Uppercase above the value on a tile, plain on a row. */
  label: string;
  value: ReactNode;
  icon?: ReactNode;
  /** Tailwind text colour for the value; defaults to foreground. */
  valueClassName?: string;
  /**
   * Makes the chip a button: bordered, hover-lifted, chevron after the value.
   * A chip without it is a flat fact tile.
   */
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  /**
   * "tile" is the raised chip for a wrapping row of facts. "row" is one line
   * of a list — label left, value right — for narrow screens where tiles
   * wrap into a ragged grid.
   */
  variant?: "tile" | "row";
  className?: string;
}

/**
 * One fact in a row of facts. Border and hover mark the pressable one; the
 * rest are flat so the two can't be confused.
 */
export const StatChip = ({
  label,
  value,
  icon,
  valueClassName,
  onClick,
  variant = "tile",
  className,
}: StatChipProps) => {
  const Tag = onClick ? "button" : "div";
  const shared = {
    type: onClick ? ("button" as const) : undefined,
    onClick,
    "aria-haspopup": onClick ? ("dialog" as const) : undefined,
  };

  if (variant === "row") {
    return (
      <Tag
        {...shared}
        className={cn(
          "flex w-full items-center justify-between gap-4 py-2 text-left text-sm",
          onClick &&
            "cursor-pointer rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-white/40",
          className,
        )}
      >
        <span className="flex min-w-0 items-center gap-2 text-foreground-muted">
          {icon && (
            <span className="flex size-4 shrink-0 items-center justify-center">
              {icon}
            </span>
          )}
          {label}
        </span>
        <span className="flex shrink-0 items-center gap-1">
          <span className={cn("font-semibold text-foreground", valueClassName)}>
            {value}
          </span>
          {onClick && <ChevronRight className="size-4 text-foreground-muted" />}
        </span>
      </Tag>
    );
  }

  return (
    <Tag
      {...shared}
      className={cn(
        "flex flex-col items-center gap-2 rounded-xl bg-surface-raised px-3 py-2 text-left",
        // On a hoverable Card the card lifts to the tile's own fill; step up.
        "transition-colors duration-200 group-hover:bg-control",
        onClick &&
          "cursor-pointer border border-edge-strong transition-colors duration-200 outline-none hover:border-edge-strongest hover:bg-control focus-visible:ring-2 focus-visible:ring-white/40",
        className,
      )}
    >
      <div className="flex flex-row items-start justify-start gap-2 w-full">
        <span className="text-tiny uppercase tracking-wider text-foreground-muted">
          {label}
        </span>
        {onClick && (
          <ChevronDown className="ml-auto size-3.5 shrink-0 text-foreground-muted" />
        )}
      </div>
      <div className="flex min-w-0 flex-row items-center justify-start gap-2 w-full">
        {icon && (
          <div className="flex size-4 shrink-0 items-center justify-center text-foreground-muted [&>svg]:size-4 [&>img]:size-4">
            {icon}
          </div>
        )}
        <span
          className={cn(
            "min-w-0 truncate text-sm font-semibold text-foreground",
            valueClassName,
          )}
        >
          {value}
        </span>
      </div>
    </Tag>
  );
};
